import {useOppsigelsesgrunner, usePlasseringer, Plassering} from "../../api/plasseringer";
import {submitOppsigelse, OppsigelseFormData} from "../../api/oppsigelser";
import LoadingSpinner from "../../components/LoadingSpinner";
import {PktAlert, PktButton, PktDatepicker, PktSelect} from "@oslokommune/punkt-react";
import React, {useEffect, useMemo, useState} from "react";
import {useNavigate} from "react-router-dom";
import {FormProvider, SubmitHandler, useForm, useFormContext} from "react-hook-form";
import {submitError} from "../../errorReport";
import RadioButtons from "../../components/RadioButtons";
import {InfoBox, InfoBoxItem} from "../../components/InfoBox";
import {getDocumentTitle} from "../../utils/documentTitle";
import {dateIsoString, isoStringRangeToLocaleStringRange} from "../../utils/datoUtil";
import {ApiError} from "../../api/apiError";

type SubmitState = undefined | 'pending' | 'completed' | 'error'

export function Oppsigelse() {
  const navigate = useNavigate()
  const [plasseringer, plasseringerStatus, plasseringerError] = usePlasseringer()
  const methods = useForm<OppsigelseFormData>()
  const {handleSubmit, control, watch, resetField} = methods

  const valgtBarnIdent = watch('barnIdent')
  const valgtPlasseringId = watch('plasseringId')

  const [submitState, setSubmitState] = useState<SubmitState>(undefined)

  useEffect(() => {
    document.title = getDocumentTitle("oppsigelse")
  })

  useEffect(() => {
    //nullstill valgt barnehage hvis man bytter barn
    resetField('plasseringId')
  }, [valgtBarnIdent])

  const barnMedPlassering = useMemo(() => {
    if (plasseringer) {
      return plasseringer.reduce((acc, val) => {
        if (!acc.some((b) => b.ident == val.barnIdent)) {
          acc.push({ident: val.barnIdent, navn: [val.barnFornavn, val.barnEtternavn].join(' ')})
        }
        return acc
      }, [] as Array<{ ident: string, navn: string }>)
    } else {
      return []
    }
  }, [plasseringer])

  const plasseringerForValgtBarn = useMemo(() => {
    if (valgtBarnIdent && plasseringer) {
      return plasseringer.filter(plassering => plassering.barnIdent === valgtBarnIdent)
    } else {
      return []
    }
  }, [valgtBarnIdent, plasseringer])

  const valgtPlassering = useMemo(() => {
    if (valgtPlasseringId && plasseringer) {
      return plasseringer.find(plassering => plassering.id == valgtPlasseringId)
    }
  }, [valgtPlasseringId, plasseringer])

  const barnFulltNavn = [valgtPlassering?.barnFornavn, valgtPlassering?.barnEtternavn].join(' ')
  const harSøkt = valgtPlassering?.harSøktOppsigelse || valgtPlassering?.oppsigelseGodkjent
  const kanSendeInn = valgtPlassering?.kanSiOpp && !harSøkt

  const submitForm: SubmitHandler<OppsigelseFormData> = (data) => {
    if(submitState === 'pending') return
    setSubmitState('pending')
    submitOppsigelse(data).then(() => {
      setSubmitState('completed')
      navigate(`/oppsigelse/kvittering`, {
        state: {
          barnFulltNavn: barnFulltNavn,
        }
      })
    }).catch(err => {
      setSubmitState('error')
      console.error('Failed to submit oppsigelse', err)
      submitError('Failed to submit oppsigelse', err)
    })
  }

  function PlasseringerError() {
    if (plasseringerError instanceof ApiError && plasseringerError.type === 'PersonMedGradering') {
      return (
        <PktAlert skin={'info'} className={'mt-size-40'} style={{maxWidth: '45rem'}}>
          Vi kan ikke vise dine barnehageplasser her. Ta kontakt med oss på
          telefon <span style={{ whiteSpace: 'nowrap' }}>23 68 08 30</span> hvis du ønsker å si opp en plass.
        </PktAlert>
      )
    } else {
      return (
        <PktAlert skin={'error'} className={'mt-size-40'} style={{maxWidth: '45rem'}}>
          Det skjedde en feil. Vennligst prøv igjen senere.
        </PktAlert>
      )
    }
  }

  return (
    <FormProvider {...methods}>
      <div className="pkt-cell pkt-cell--span12">

        <h1 className={'pkt-txt-40'}>
          Oppsigelse av plass
        </h1>
        {plasseringerStatus === 'pending' && (
          <LoadingSpinner/>
        )}
        {plasseringerStatus === 'error' && (
          <PlasseringerError/>
        )}
        {plasseringerStatus === 'ready' && (
          <>
            <form onSubmit={handleSubmit(submitForm)}>

              <h2 className={'pkt-txt-16-light mt-size-60 mb-size-20'}>Steg 1 av 3</h2>
              <RadioButtons
                control={control}
                name={'barnIdent'}
                label={'Hvilket barn gjelder oppsigelsen?'}
                options={barnMedPlassering.map(barn => ({
                  label: barn.navn,
                  value: barn.ident
                }))}
              />
              {barnMedPlassering.length === 0 && (
                <PktAlert skin={'info'} className={'mt-size-20'} style={{maxWidth: '50rem'}}>
                  Vi finner ingen barnehageplasser registrert på deg i Oslo kommune.
                </PktAlert>
              )}

              {valgtBarnIdent && (
                <>
                  <h2 className={'pkt-txt-16-light mt-size-60 mb-size-20'}>Steg 2 av 3</h2>
                  <RadioButtons
                    control={control}
                    name={'plasseringId'}
                    label={'Hvilken barnehageplass vil du si opp?'}
                    options={plasseringerForValgtBarn.map(plassering => ({
                      label: plassering.barnehageNavn,
                      value: plassering.id.toString(),
                      helptext: isoStringRangeToLocaleStringRange(plassering.startDato, plassering.sluttDato)
                    }))}
                  />
                </>
              )}

              {valgtPlassering && harSøkt && (
                <PktAlert skin={"warning"} className={'mt-size-40'} style={{maxWidth: '50rem'}}>
                  Oppsigelse er allerede sendt inn for denne plassen
                </PktAlert>
              )}

              {valgtPlassering && !valgtPlassering.kanSiOpp && !harSøkt && (
                <PktAlert skin={"warning"} className={'mt-size-40'} style={{maxWidth: '50rem'}}>
                  Kun fakturamottaker kan sende inn oppsigelse
                </PktAlert>
              )}

              {valgtPlassering && kanSendeInn && (
                <>
                  <h2 className={'pkt-txt-16-light mt-size-60 mb-size-20'}>Steg 3 av 3</h2>
                  <Steg3 plassering={valgtPlassering} submitState={submitState}/>
                </>
              )}
            </form>
          </>
        )}

      </div>
    </FormProvider>
  )
}

function Steg3({plassering, submitState}: { plassering: Plassering, submitState: SubmitState }) {

  const {register, formState, watch} = useFormContext<OppsigelseFormData>()
  const [oppsigelsesgrunner, oppsigelsesgrunnerStatus] = useOppsigelsesgrunner(plassering.id)
  const valgtOppsigelsesgrunnId = watch('begrunnelseId')
  const valgtOppsigelsesgrunnTekst = oppsigelsesgrunner?.find(o => {
    return o.id.toString() === valgtOppsigelsesgrunnId
  })?.beskrivelse
  const barnFulltNavn = [plassering?.barnFornavn, plassering?.barnEtternavn].join(' ')
  const startDatoString = plassering?.startDato && new Date(plassering.startDato).toLocaleDateString('no', {dateStyle: 'long'})
  const sluttDatoString = plassering?.sluttDato && new Date(plassering.sluttDato).toLocaleDateString('no', {dateStyle: 'long'})

  const todayDateIsoString = useMemo(() => {
    const now = new Date()
    return dateIsoString(now)
  }, [])

  if (oppsigelsesgrunnerStatus === 'pending') {
    return <div className="pkt-cell pkt-cell--span12">
      <LoadingSpinner/>
    </div>
  }

  if (oppsigelsesgrunnerStatus === 'error') {
    return (
      <div className="pkt-cell pkt-cell--span12">
        <PktAlert skin={'error'} compact={true}>
          Det skjedde en feil. Vennligst prøv igjen senere.
        </PktAlert>
      </div>
    )
  }

  return (
    <>
      <h4 className='pkt-txt-18-medium'>Bekreft oppsigelse</h4>
      <div style={{maxWidth: '50rem'}}>
        <p>
          Kommunale barnehager har én måneds oppsigelsestid fra den 1. i måneden. Oppsigelsestiden for private
          barnehager
          finner du i den enkelte barnehage sine vedtekter.
        </p>
        <p>
          Hvis barnet ikke har begynt i barnehagen ennå, setter du startdatoen fra tilbudsbrevet som siste dag i
          barnehagen.
        </p>
        <p>
          Dere blir bedt om å oppgi grunn for oppsigelse. Hvis dere skal flytte ut av Oslo blir eventuelle søknader om
          bytte av barnehage i Oslo automatisk avsluttet.
        </p>

      </div>

      <InfoBox className={`mt-size-30 p-size-20`}>
        <InfoBoxItem title={'Navn'}>{barnFulltNavn}</InfoBoxItem>
        <InfoBoxItem title={'Barnehage'}>{plassering?.barnehageNavn}</InfoBoxItem>
        <InfoBoxItem title={'Plassering'}>{[startDatoString, sluttDatoString].filter(Boolean).join(' - ')}</InfoBoxItem>
        <InfoBoxItem title={'Plasstype'}>{plassering?.plasstype}</InfoBoxItem>
      </InfoBox>

      <PktSelect
        className={'mt-size-30'}
        id={"oppsigelsesgrunn"}
        label={"Hvorfor sier du opp plassen?"}
        hasError={!!formState.errors.begrunnelseId}
        errorMessage={formState.errors.begrunnelseId?.message}
        defaultValue={''}
        {...register('begrunnelseId', {
          required: "Dette feltet må fylles ut",
        })}
      >
        <option disabled value={''}>
          Velg
        </option>
        {oppsigelsesgrunner?.map(oppsigelsesgrunn => (
          <option key={oppsigelsesgrunn.id} value={oppsigelsesgrunn.id}>
            {oppsigelsesgrunn.beskrivelse}
          </option>
        ))}
      </PktSelect>

      {valgtOppsigelsesgrunnTekst === 'Flytting ut av Oslo' && (
        <PktAlert skin={"info"} className={'mt-size-40'} style={{maxWidth: '50rem'}}>
          Når dere flytter ut av Oslo, blir eventuelle søknader om bytte av barnehage i Oslo automatisk avsluttet.
        </PktAlert>
      )}

      <PktDatepicker
        className={'mt-size-30'}
        label={"Når ønsker dere å avslutte plassen?"}
        helptext={"Oppgi ønsket dato. En saksbehandler vil sette en endelig sluttdato som følger barnehagens oppsigelsestid."}
        hasError={!!formState.errors.sluttDato}
        errorMessage={formState.errors.sluttDato?.message}
        min={todayDateIsoString}
        {...register('sluttDato', {
          required: "Dette feltet må fylles ut",
          min: {value: todayDateIsoString, message: "Dato kan ikke være før i dag"},
          validate: (dateString) => {
            const startDato = new Date(plassering.startDato)
            const valgtDato = new Date(dateString)
            if(valgtDato < startDato) {
              return "Siste dag kan ikke være før plasseringens startdato. Hvis barnet ikke har begynt i barnehagen ennå, setter du startdatoen fra tilbudsbrevet som siste dag i barnehagen."
            } else {
              return true
            }
          }
        })}
      />

      {submitState === 'error' && (
        <PktAlert skin={'error'} className={'mt-size-40'}>
          Det skjedde en feil ved innsending, prøv igjen senere.
        </PktAlert>
      )}

      <PktButton className={'mt-size-40 mb-size-100'} type={"submit"} isLoading={submitState === 'pending'}>
        Send inn oppsigelse
      </PktButton>

    </>
  )
}
